/**
 * Bot notes synced from Intercom sometimes carry the raw JSON dump of the
 * Anthropic content blocks (`[{"type": "text", ...}, {"type": "tool_use", ...}]`)
 * instead of plain prose. We detect that shape so the UI can render the
 * blocks structured rather than as a wall of JSON text.
 */
export type ContentBlock =
	| { type: 'text'; text: string }
	| { type: 'tool_use'; name: string; input: Record<string, unknown>; id?: string }
	| { type: 'unknown'; raw: Record<string, unknown> };

const isRecord = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === 'object' && !Array.isArray(value);

const toContentBlock = (value: unknown): ContentBlock | null => {
	if (!isRecord(value)) return null;
	if (value.type === 'text' && typeof value.text === 'string') return { type: 'text', text: value.text };
	if (value.type === 'tool_use' && typeof value.name === 'string' && isRecord(value.input)) {
		return {
			type: 'tool_use',
			name: value.name,
			input: value.input,
			id: typeof value.id === 'string' ? value.id : undefined,
		};
	}
	if (typeof value.type === 'string') return { type: 'unknown', raw: value };
	return null;
};

/**
 * Returns the parsed blocks when `content` is a JSON array of Anthropic
 * content blocks, or `null` for anything else so callers fall back to the
 * plain-text rendering.
 */
export const parseContentBlocks = (content: string): ContentBlock[] | null => {
	const trimmed = content.trim();

	if (!trimmed.startsWith('[')) return null;
	try {
		const parsed: unknown = JSON.parse(trimmed);

		if (!Array.isArray(parsed) || parsed.length === 0) return null;
		const blocks = parsed.map(toContentBlock);

		return blocks.every((block): block is ContentBlock => block !== null) ? blocks : null;
	} catch {
		return null;
	}
};
